import type { Ref } from "react";

import { useTranslation } from "react-i18next";

import type { EventInviteCode } from "@/api/bale.zod";

import { useCreateEventInviteCode, useDeleteEventInviteCode, useListEventInviteCodes } from "@/api/bale";
import { ActionBox } from "@/components/ActionBox";
import { Button } from "@/components/Button";
import { CardSubtitleHeader } from "@/components/Card";
import { CopyButton } from "@/components/CopyButton";
import { Dialog } from "@/components/Dialog";
import { Form } from "@/components/Form";
import { Icon } from "@/components/Icon";
import { IconButton } from "@/components/IconButton";
import { Missing } from "@/components/Missing";
import { useCurrentEventSuspense } from "@/pages/EventPage/hooks/useCurrentEvent";

export function PublicSharesModal({ ref, onClose }: { ref: Ref<HTMLDialogElement>; onClose?: () => void }) {
  const { t } = useTranslation();
  const event = useCurrentEventSuspense({ query: { select: (data) => data.data.event } });
  const inviteCodes = useListEventInviteCodes(event.data.id, {
    query: { select: (data) => data.data.event_invite_codes },
  });

  const createInviteCode = useCreateEventInviteCode();

  return (
    <Dialog ref={ref} onClose={onClose} closeOnOutside className="w-2xl">
      <CardSubtitleHeader>{t("manage-invite-codes")}</CardSubtitleHeader>
      <div className="flex flex-col">
        <p className="px-4 font-disinterest">{t("manage-invite-codes-description")}</p>
        <p className="mt-4 mb-2 px-4 font-hint">{t("existing-invite-codes")}</p>
        <div className="flex flex-col border-t border-divider empty:hidden">
          {inviteCodes.isSuccess && inviteCodes.data.map((invite) => <InviteItem key={invite.id} invite={invite} />)}
        </div>
        {inviteCodes.isSuccess && inviteCodes.data.length === 0 && <Missing>{t("no-invite-codes")}</Missing>}
        <Form
          error={createInviteCode.error}
          className="flex flex-row flex-wrap gap-x-4 gap-y-2 p-4"
          onSubmit={() => createInviteCode.mutate({ eventId: event.data.id })}
        >
          <Button className="min-w-0 grow-1 truncate" type="submit">
            {t("create-invite-code")}
          </Button>
        </Form>
      </div>
    </Dialog>
  );
}

function InviteItem({ invite }: { invite: EventInviteCode }) {
  const { t } = useTranslation();
  const deleteInviteCode = useDeleteEventInviteCode();

  return (
    <div className="flex flex-row flex-wrap items-center justify-between border-b border-divider px-4 py-2 text-start">
      <p className="font-mono font-interest" aria-label={t("invite-code")}>
        {invite.id}
      </p>
      <ActionBox>
        <CopyButton value={invite.url}>
          <Icon name="link" />
          {t("copy-invite-url")}
        </CopyButton>
        <IconButton
          type="button"
          className="text-danger"
          name="trash"
          onClick={() => deleteInviteCode.mutate({ inviteCodeId: invite.id })}
        />
      </ActionBox>
    </div>
  );
}
